import { Button } from "@headerhawk/ui";

interface AccountGreetingProps {
  /**
   * Whether this account was created by the sign-in that just happened. From
   * `useIsNewAccount`, not straight from `/auth/me` — the raw flag is only true
   * on the response that provisioned.
   */
  isNewAccount: boolean;
  email: string;
  onSignOut: () => void;
}

/**
 * Says, in plain words, whether the address that just verified a code is a new
 * account or an existing one.
 *
 * This is the cost of merging sign-in and sign-up into one flow. Closing the
 * account-enumeration oracle means an unknown address no longer hears "email
 * does not exist" — so a mistyped address quietly creates an empty account
 * instead of being refused. Nothing else on the way in distinguishes the two,
 * which is why the first screen after verification does.
 *
 * Rendered by the onboarding layout: onboarding is where a brand-new account
 * lands, and where a returning user with no site lands too. The sign-out button
 * is part of the message rather than a nicety — onboarding has no other way off
 * the page, so without it the advice would be unfollowable.
 */
export function AccountGreeting({
  isNewAccount,
  email,
  onSignOut,
}: AccountGreetingProps) {
  return (
    <div
      role="status"
      data-testid="account-greeting"
      className="flex flex-wrap items-center justify-between gap-3 rounded-md border bg-muted/40 px-4 py-3 text-sm text-muted-foreground"
    >
      {isNewAccount ? (
        <>
          <span>
            New HeaderHawk account for{" "}
            <span className="font-medium text-foreground">{email}</span>. If
            that is not the address you meant, sign out and start again —
            nothing has been set up yet.
          </span>
          <Button variant="outline" size="sm" onClick={onSignOut}>
            Sign out
          </Button>
        </>
      ) : (
        <span>
          Welcome back. Signed in as{" "}
          <span className="font-medium text-foreground">{email}</span>.
        </span>
      )}
    </div>
  );
}
